import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./Cases.module.css";
import { casesActions } from "@_actions/cases.actions";
import config from "@_configs/api.config";
import CaseDetails from "@_components/CaseDetails/CaseDetails";
import { caseActions } from "@_actions/case.actions";
import AddCasePopup from "@_components/popups/AddCasePopup/AddCasePopup";
import caseStates from "@_configs/caseStates.config";
import { caseConstants } from "@_constants/case.constants";
import "scrollbar.css";
import Scrollbar from "react-perfect-scrollbar-z";
import { sidebarActions } from "@_actions/sidebar.actions";
import { customerActions } from "@_actions/customer.actions";
import CasesReportPopup from "@_components/popups/CasesReportPopup/CasesReportPopup";
import CasesToolbar from "./component/CasesToolbar";
import CaseList from "./component/CaseList";
import filterConfig from "./component/filterConfig";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";

const CASE_STATE_ROUTES = {
  active: caseConstants.CASE_STATE_ACTIVE,
  paid: caseConstants.CASE_STATE_PAID,
  standby: caseConstants.CASE_STATE_STANDBY,
  cancelled: caseConstants.CASE_STATE_CANCELLED,
  payment_processing: caseConstants.CASE_STATE_PAYMENT_PROCESSING,
  valid_license: caseConstants.CASE_STATE_VALID_LICENSE,
};

function Cases() {
  const casesReducer = useSelector((state) => state.casesReducer);
  const sidebarReducer = useSelector((state) => state.sidebarReducer);

  const [searchValue, setSearchValue] = useState("");
  const [selectedSearchTag, setSelectedSearchTag] = useState(null);
  const [loadNow, setLoadNow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSearchObject, setCurrentSearchObject] = useState({});
  const [allLoaded, setAllLoaded] = useState(false);
  const [activeFilter, setActiveFilter] = useState(
    caseConstants.CASE_FILTER_NONE,
  );
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [lastSidebarStatus, setLastSidebarStatus] = useState("CASES");
  const [openCasesReportPopup, setOpenCasesReportPopup] = useState(false);

  const [openAddCasePopup, setOpenAddCasePopup] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const pathParts = location.pathname.split("/").filter(Boolean);
  const urlParams = new URLSearchParams(location.search);
  const searchDebounceRef = useRef(null);

  const casesSearchTag = [
    { value: "case_id", label: "Case ID" },
    { value: "reference_number", label: "Reference Number" },
    { value: "domain", label: "Domain" },
    { value: "image", label: "Image" },
    { value: "tag", label: "Tag" },
  ];

  const caseStatesArray = Object.keys(caseStates);

  useEffect(() => {
    dispatch(customerActions.getCredits());
    if (pathParts[0] === "cases") {
      if (
        pathParts[1] &&
        caseStatesArray.some(
          (state) => state.toLowerCase() === pathParts[1].toLowerCase(),
        )
      ) {
        const mappedState = CASE_STATE_ROUTES[pathParts[1].toLowerCase()];
        if (mappedState) {
          dispatch(
            sidebarActions.updateStatus(mappedState, optionalFilter()),
          );
        } else {
          history.push("/page_not_found");
        }
      } else {
        dispatch(sidebarActions.updateStatus("CASES", optionalFilter()));
      }
    }
    // eslint-disable-next-line
  }, [dispatch]);

  const resetScrollTop = () => {
    const scrollNode = document.querySelector("#sb>div");
    if (scrollNode) scrollNode.scrollTop = 0;
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const optionalFilter = () => {
    const urlFilter = urlParams.get("filter");
    switch (urlFilter) {
      case "unread_emails":
        activateFilter(caseConstants.CASE_FILTER_MAIL);
        return caseConstants.CASE_FILTER_MAIL;

      case "overdue_reminders":
        activateFilter(caseConstants.CASE_FILTER_REMINDER);
        return caseConstants.CASE_FILTER_REMINDER;

      case "uploaded_licenses":
        activateFilter(caseConstants.CASE_FILTER_UPLOADED_LICENSE);
        return caseConstants.CASE_FILTER_UPLOADED_LICENSE;

      case "failed_letters":
        activateFilter(caseConstants.CASE_FILTER_FAILED_LETTERS);
        return caseConstants.CASE_FILTER_FAILED_LETTERS;

      case "failed_emails":
        activateFilter(caseConstants.CASE_FILTER_FAILED_EMAIL);
        return caseConstants.CASE_FILTER_FAILED_EMAIL;

      case "paused":
        activateFilter(caseConstants.CASE_FILTER_PAUSED);
        return caseConstants.CASE_FILTER_PAUSED;

      case "require_attention":
        activateFilter(caseConstants.CASE_FILTER_REQUIRE_ATTENTION);
        return caseConstants.CASE_FILTER_REQUIRE_ATTENTION;

      case "idle":
        activateFilter(caseConstants.CASE_FILTER_IDLE_CASES);
        return caseConstants.CASE_FILTER_IDLE_CASES;

      default:
        return null;
    }
  };

  const createSearchObject = useCallback(
    (searchValue, selectedSearchTag, activeFilter) => {
      const searchObject = {};
      searchObject.sort = {
        key: "last_case_activity_timestamp",
        ascending:
          sidebarReducer[sidebarReducer.status.toLowerCase() + "Asc"] || false,
      };
      // The API expects every filter flag present as a boolean. Start with all
      // flags off, then enable the single one for the active filter (derived
      // from the shared filterConfig -> apiKey mapping).
      let filter = {
        unread_emails: false,
        unchecked_licenses: false,
        failed_letters: false,
        overdue_reminders: false,
        failed_emails: false,
        paused_scheduled_emails: false,
        action_required: false,
        idle: false,
        failed_payments: false,
      };
      const activeApiKey = filterConfig[activeFilter]?.apiKey;
      if (activeApiKey) {
        filter[activeApiKey] = true;
      }
      if (searchValue && searchValue.trim().length > 0) {
        filter = {
          ...filter,
          search: {
            search_string: searchValue,
            search_mode: "contains",
            ...(selectedSearchTag
              ? { search_by: selectedSearchTag.value }
              : null),
          },
        };
        if (
          searchValue[0] === '"' &&
          searchValue[searchValue.length - 1] === '"'
        ) {
          filter.search.search_mode = "exact";
          filter.search.search_string = searchValue.slice(1, -1);
        }
      }

      searchObject.filter = filter;
      searchObject.paging = { page: 1, page_size: config.PAGE_SIZE };
      if (sidebarReducer.status !== "CASES") {
        searchObject.filter.state = sidebarReducer.status;
      }
      return searchObject;
    },
    [sidebarReducer],
  );

  const activateFilter = (filter) => {
    setActiveFilter(filter);
    setFilterMenuOpen(false);
  };

  useEffect(() => {
    // check if current page is a case page

    if (Object.keys(caseStates).indexOf(sidebarReducer.status) === -1) return;

    // A sidebar navigation can request a filter (optionalFilter). Apply it as
    // the active filter and consume it. Use it directly for this run's search
    // so the fetch is correct before the activeFilter state settles.
    let effectiveFilter = activeFilter;
    if (
      sidebarReducer.optionalFilter &&
      filterConfig[sidebarReducer.optionalFilter]
    ) {
      effectiveFilter = sidebarReducer.optionalFilter;
      activateFilter(sidebarReducer.optionalFilter);
      dispatch(sidebarActions.resetFilter());
    }

    const s = createSearchObject(searchValue, selectedSearchTag, effectiveFilter);
    setCurrentSearchObject(s);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      dispatch(casesActions.getCases(s, true));
    }, 500);
    resetScrollTop();

    if (
      sidebarReducer.status !== lastSidebarStatus &&
      !sidebarReducer.optionalFilter
    ) {
      resetScrollTop();
      handleClearClick();
      setActiveFilter(caseConstants.CASE_FILTER_NONE);
    }
    setLastSidebarStatus(sidebarReducer.status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sidebarReducer.status,
    casesReducer.ascending,
    sidebarReducer.casesAsc,
    sidebarReducer.standbyAsc,
    sidebarReducer.paidAsc,
    sidebarReducer.activeAsc,
    sidebarReducer.cancelledAsc,
    sidebarReducer.valid_licenseAsc,
    sidebarReducer.payment_processingAsc,
    sidebarReducer.optionalFilter,
    searchValue,
    activeFilter,
  ]);

  useEffect(() => {
    if (casesReducer.status.CASES_GET === "success") {
      if (
        casesReducer.count > 0 &&
        casesReducer.cases.length === casesReducer.count
      )
        setAllLoaded(true);
      dispatch(casesActions.clearStateMessage("CASES_GET"));
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casesReducer.status]);

  const loadFunc = useCallback(() => {
    if (
      !isLoading &&
      casesReducer.status.CASES_GET !== "request" &&
      casesReducer.count > 0 &&
      casesReducer.count !== casesReducer.cases.length
    ) {
      setIsLoading(true);
      let s;
      if (currentSearchObject.paging) {
        // Normal path: advance to the next page based on the page we last
        // fetched. Copy immutably so we never mutate the stored search object.
        s = {
          ...currentSearchObject,
          paging: {
            ...currentSearchObject.paging,
            page: currentSearchObject.paging.page + 1,
          },
        };
      } else {
        // Fallback: no prior search object (e.g. first load raced pagination).
        // Rebuild it from the current search/filter state and derive the next
        // page from how many rows are already loaded.
        s = createSearchObject(searchValue, selectedSearchTag, activeFilter);
        const pageSize = s.paging.page_size || config.PAGE_SIZE;
        s.paging.page =
          Math.floor(casesReducer.cases.length / pageSize) + 1;
      }

      dispatch(casesActions.getCases(s));
      setCurrentSearchObject(s);
    }
  }, [
    casesReducer.cases.length,
    casesReducer.count,
    casesReducer.status.CASES_GET,
    createSearchObject,
    currentSearchObject,
    dispatch,
    isLoading,
    searchValue,
    selectedSearchTag,
    activeFilter,
  ]);

  useEffect(() => {
    setLoadNow(false);
    if (loadNow && allLoaded === false) {
      loadFunc();
    }
  }, [allLoaded, loadFunc, loadNow]);

  const hasMore =
    casesReducer.count > 0 &&
    casesReducer.cases.length < casesReducer.count;

  const handleSearchInputChange = (event) => {
    if (!selectedSearchTag) return;
    setSearchValue(event.target.value);
  };
  const handleSearchTagSelect = (option) => {
    setSelectedSearchTag(option);
    setSearchValue("");
  };
  const handleClearClick = () => {
    setSelectedSearchTag(null);
    setSearchValue("");
  };

  const handleOpenAddCasePopup = () => {
    dispatch(caseActions.clearStateMessage("CASE_ADD_FINDING"));
    setOpenAddCasePopup(true);
  };

  const handleFilterIconClicked = () => {
    if (activeFilter === caseConstants.CASE_FILTER_NONE) {
      setFilterMenuOpen(true);
    } else {
      activateFilter(caseConstants.CASE_FILTER_NONE);
    }
  };

  const handleSortToggle = () => {
    const ascKey = sidebarReducer.status.toLowerCase() + "Asc";
    const nextAscending = !sidebarReducer[ascKey];
    localStorage.setItem(ascKey, nextAscending);
    dispatch(sidebarActions.sortCases(ascKey, nextAscending));
  };

  return (
    <div className={styles.root}>
      <div className={styles.case_list_container}>
        <CasesToolbar
          searchValue={searchValue}
          selectedSearchTag={selectedSearchTag}
          casesSearchTag={casesSearchTag}
          onSearchInputChange={handleSearchInputChange}
          onSearchTagSelect={handleSearchTagSelect}
          onClearClick={handleClearClick}
          ascending={sidebarReducer[sidebarReducer.status.toLowerCase() + "Asc"]}
          onSortToggle={handleSortToggle}
          onExport={() => setOpenCasesReportPopup(true)}
          onAddCase={handleOpenAddCasePopup}
          activeFilter={activeFilter}
          onFilterIconClicked={handleFilterIconClicked}
          filterCount={casesReducer.count}
          filterMenuOpen={filterMenuOpen}
          onActivateFilter={activateFilter}
          onCloseFilterMenu={() => setFilterMenuOpen(false)}
        />
        <CaseList
          cases={casesReducer.cases}
          casesGetStatus={casesReducer.status.CASES_GET}
          selections={sidebarReducer.selections}
          sidebarStatus={sidebarReducer.status}
          onNext={loadFunc}
          hasMore={hasMore}
          onScrollLoad={() => setLoadNow(true)}
        />
      </div>
      <div className={styles.case_details_container}>
        <Scrollbar
          options={{ suppressScrollX: true }}
          maxHeight="calc(100vh - 141px)"
        >
          <CaseDetails />
        </Scrollbar>
      </div>
      <AddCasePopup
        open={openAddCasePopup}
        close={() => setOpenAddCasePopup(false)}
      />
      <CasesReportPopup
        open={openCasesReportPopup}
        close={() => setOpenCasesReportPopup(false)}
      />
    </div>
  );
}

export default Cases;
